import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ChevronDown, ChevronRight, Hourglass } from "lucide-react";

const FLAG = {
  breached: { label: "Breached", cls: "bg-red-600/15 text-red-600" },
  at_risk: { label: "At risk", cls: "bg-amber-500/15 text-amber-600" },
  stalled: { label: "Stalled", cls: "bg-purple-500/15 text-purple-600" },
};
const DOT = { urgent: "bg-red-600", high: "bg-orange-500", medium: "bg-blue-500", low: "bg-slate-400" };

const Count = ({ n, label, cls, testid }) => (
  <div className="border border-border rounded-sm p-3" data-testid={testid}>
    <div className={`text-2xl font-condensed font-extrabold ${n ? cls : "text-muted-foreground"}`}>{n}</div>
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

function GroupRow({ g, open, toggle, onOpen }) {
  return (
    <div className="border border-border rounded-sm" data-testid={`attention-group-${g.group_id}`}>
      <button onClick={toggle} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent/60 transition-colors" data-testid={`attention-group-toggle-${g.group_id}`}>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-sm font-semibold flex-1">{g.group_name}</span>
        {["breached", "at_risk", "stalled"].map((f) => g[f] ? <span key={f} className={`text-[11px] px-2 py-0.5 rounded-sm font-medium ${FLAG[f].cls}`}>{g[f]} {FLAG[f].label.toLowerCase()}</span> : null)}
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border">
          {g.tickets.map((t) => (
            <li key={t.id}>
              <button onClick={() => onOpen(t.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/60 transition-colors" data-testid={`attention-ticket-${t.id}`}>
                <span className={`h-2 w-2 rounded-full shrink-0 ${DOT[t.priority] || DOT.low}`} />
                <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                <span className="truncate flex-1">{t.subject}</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">{t.assignee_name || "Unassigned"}</span>
                {t.flags.map((f) => <span key={f} className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${FLAG[f].cls}`}>{f === "stalled" ? `Quiet ${t.quiet_days}d` : FLAG[f].label}</span>)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NeedsAttention({ className = "" }) {
  const [data, setData] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/tickets/attention").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (!data) return null;
  const toggle = (id) => setOpenGroups((o) => ({ ...o, [id]: !o[id] }));
  return (
    <div className={`border border-border bg-card rounded-sm p-4 ${className}`} data-testid="needs-attention-tile">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className={`h-4 w-4 ${data.total ? "text-primary" : "text-muted-foreground"}`} />
        <span className="text-sm font-semibold">Monge Ticketing — Needs Attention</span>
        <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1"><Hourglass className="h-3 w-3" />Stalled = Waiting {data.waiting_reminder_days}+ days quiet{data.auto_resolve_days ? ` · auto-resolves ${data.auto_resolve_days}d after reminder` : ""}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <Count n={data.totals.breached} label="SLA breached" cls="text-red-600" testid="attention-breached" />
        <Count n={data.totals.at_risk} label="SLA at risk" cls="text-amber-600" testid="attention-at-risk" />
        <Count n={data.totals.stalled} label="Stalled waiting" cls="text-purple-600" testid="attention-stalled" />
      </div>
      {data.groups.length ? (
        <div className="space-y-2" data-testid="attention-groups">
          {data.groups.map((g) => <GroupRow key={g.group_id} g={g} open={!!openGroups[g.group_id]} toggle={() => toggle(g.group_id)} onOpen={(id) => navigate(`/tickets?open=${id}`)} />)}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="attention-empty">All clear — no open tickets are breached, at risk, or stalled.</p>
      )}
    </div>
  );
}
